import { Spin, Empty, Typography, Button, Card, Row, Col, Space, Switch, Modal, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { getUserType } from '../../helpers/userDetails';
import BatchVouchersTable from '../Items/Table/BatchVouchersTable';
import { formatDate } from '../../helpers/date';
import '../Items/Table/index.css';

interface BatchDetails {
  intPkBatchID: number;
  intPkCompanyID: number;
  batchName: string;
  intPkPackageID: number;
  packageName: string;
  batchLength: number;
  batchProcessStatus: 'PENDING' | 'PROCESSED';
  speedUp: number;
  speedDown: number;
  speedUnit: 'K' | 'M' | 'G';
  maxDataVolumeLimit: number | null;
  maxDataVolumeUnit: 'K' | 'M' | 'G' | null;
  voucherCharactersLimit: number;
  voucherNumberType: 'NUMERIC' | 'ALPHANUMERIC';
  packageDurationEnum: 'HOURS' | 'DAYS';
  packageDurationDays: number;
  packageDurationHours: number;
  enable: number;
  dateCreated?: string;
  totalVouchers?: number;
  usedVouchers?: number;
  remainingVouchers?: number;
  packages?: {
    packageName: string;
    speedUp: number;
    speedDown: number;
    speedUnit: string;
  };
  company?: {
    companyName: string;
  };
}

const SingleBatchComponent = () => {
  const { intPkBatchID } = useParams();
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();
  const [batchDetails, setBatchDetails] = useState<BatchDetails | null>(null);
  const [vouchers, setVouchers] = useState<BatchVouchersTableDataType[]>([]);
  const [isToggleConfirmOpen, setIsToggleConfirmOpen] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState<number | null>(null);
  const [switchChecked, setSwitchChecked] = useState<boolean>(false);
  const lastProcessedToggleResponse = useRef<any>(null);
  const lastProcessedToggleErrorCount = useRef(0);
  const lastProcessedResetVoucherResponse = useRef<any>(null);
  const lastProcessedResetVoucherErrorCount = useRef(0);
  const hasProcessedResetSuccess = useRef(false);
  const lastProcessedToggleVoucherResponse = useRef<any>(null);
  const hasProcessedToggleSuccess = useRef(false);
  const lastProcessedToggleVoucherErrorCount = useRef(0);

  // Pagination, search, and sorting state
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
  const [searchMac, setSearchMac] = useState<string>('');
  const [searchVoucher, setSearchVoucher] = useState<string>('');
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('batches/details');

  const [
    toggleStatusData,
    toggleStatusResponse,
    toggleStatusError,
    toggleStatusLoading,
    toggleStatusMessage,
    toggleStatusErrorCount,
  ] = useAxiosPost<ResponseProp>('batches/toggle-status');

  const [fetchVouchersData, vouchersResponse, vouchersError, vouchersLoading, vouchersMessage, vouchersErrorCount] =
    useAxiosPost<ResponseProp>('batches/vouchers');

  const [
    resetVoucherData,
    resetVoucherResponse,
    resetVoucherError,
    resetVoucherLoading,
    resetVoucherMessage,
    resetVoucherErrorCount,
  ] = useAxiosPost<ResponseProp>('batches/reset-voucher-status');

  const [
    toggleVoucherData,
    toggleVoucherResponse,
    toggleVoucherError,
    toggleVoucherLoading,
    toggleVoucherMessage,
    toggleVoucherErrorCount,
  ] = useAxiosPost<ResponseProp>('vouchers/toggle-status');

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState<number | string | null>(null);
  const [isVoucherToggleConfirmOpen, setIsVoucherToggleConfirmOpen] = useState(false);
  const [selectedVoucherToggle, setSelectedVoucherToggle] = useState<{
    id: number | string;
    enable: number;
  } | null>(null);

  useEffect(() => {
    if (intPkBatchID) {
      postApiData({ id: Number(intPkBatchID) });
    }
  }, [intPkBatchID]);

  // Fetch vouchers only when batch ID changes (initial load). Page/search/sort changes are handled in their callbacks so we never double-fetch.
  useEffect(() => {
    if (intPkBatchID) {
      fetchVouchersData({
        id: Number(intPkBatchID),
        page: 1,
        pageSize: pagination.pageSize,
        searchMac: searchMac || undefined,
        searchVoucher: searchVoucher || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined,
      } as any);
    }
  }, [intPkBatchID]);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (responseData) {
        setBatchDetails(responseData);
        setSwitchChecked(Boolean(responseData.enable));
      }
    }
  }, [apiResponse]);

  // Handle toggle status success
  useEffect(() => {
    if (toggleStatusResponse !== null && toggleStatusResponse !== lastProcessedToggleResponse.current) {
      const responseData: any = toggleStatusResponse.responseData;
      if (responseData) {
        setBatchDetails(responseData);
        setSwitchChecked(Boolean(responseData.enable));
        openNotification(
          'success',
          'Status Updated',
          `Batch ${responseData.enable === 1 ? 'enabled' : 'disabled'} successfully`,
        );
      }
      setIsToggleConfirmOpen(false);
      setPendingToggleValue(null);
      lastProcessedToggleResponse.current = toggleStatusResponse;
    }
  }, [toggleStatusResponse, openNotification]);

  // Handle toggle status error
  useEffect(() => {
    if (
      toggleStatusError !== null &&
      toggleStatusErrorCount !== 0 &&
      toggleStatusErrorCount !== lastProcessedToggleErrorCount.current
    ) {
      openNotification('error', 'Failed to Update Status', toggleStatusError);
      setIsToggleConfirmOpen(false);
      setPendingToggleValue(null);
      lastProcessedToggleErrorCount.current = toggleStatusErrorCount;
      // Revert switch to original state
      if (batchDetails) {
        setSwitchChecked(Boolean(batchDetails.enable));
      }
    }
  }, [toggleStatusError, toggleStatusErrorCount, openNotification, batchDetails]);

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0) {
      openNotification('error', 'Failed to Load Batch', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle vouchers response
  useEffect(() => {
    if (vouchersResponse !== null) {
      const responseData: any = vouchersResponse.responseData;
      if (responseData) {
        if (responseData.vouchers && Array.isArray(responseData.vouchers)) {
          setVouchers(responseData.vouchers);
        } else if (Array.isArray(responseData)) {
          // Backward compatibility: if response is directly an array
          setVouchers(responseData);
        } else {
          setVouchers([]);
        }

        // Update pagination from response so total and pageSize stay in sync; keep current from response so active page highlights
        const paginationData = responseData.pagination || {
          current: responseData.page,
          pageSize: responseData.pageSize,
          total: responseData.total,
        };
        if (paginationData && (paginationData.total !== undefined || responseData.pagination)) {
          setPagination((prev) => ({
            current: paginationData.current ?? prev.current,
            pageSize: paginationData.pageSize ?? prev.pageSize,
            total: paginationData.total ?? prev.total,
          }));
        }
      } else {
        setVouchers([]);
      }
    }
  }, [vouchersResponse]);

  // Handle vouchers error
  useEffect(() => {
    if (vouchersError !== null && vouchersErrorCount !== 0) {
      openNotification('error', 'Failed to Load Vouchers', vouchersError);
    }
  }, [vouchersError, vouchersErrorCount, openNotification]);

  // Handle reset voucher success
  useEffect(() => {
    if (
      resetVoucherResponse !== null &&
      resetVoucherResponse !== lastProcessedResetVoucherResponse.current &&
      !hasProcessedResetSuccess.current
    ) {
      hasProcessedResetSuccess.current = true;
      openNotification('success', 'Voucher Reset', 'Voucher status has been reset to reuse successfully');
      setIsResetConfirmOpen(false);
      setSelectedVoucherId(null);
      lastProcessedResetVoucherResponse.current = resetVoucherResponse;
      // Refresh vouchers list
      if (intPkBatchID) {
        setTimeout(() => {
          fetchVouchersData({
            id: Number(intPkBatchID),
            page: pagination.current,
            pageSize: pagination.pageSize,
            searchMac: searchMac || undefined,
            searchVoucher: searchVoucher || undefined,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
          } as any);
        }, 100);
      }
    }
  }, [resetVoucherResponse]);

  // Reset the flag when resetVoucherResponse becomes null
  useEffect(() => {
    if (resetVoucherResponse === null) {
      hasProcessedResetSuccess.current = false;
    }
  }, [resetVoucherResponse]);

  // Handle reset voucher error
  useEffect(() => {
    if (
      resetVoucherError !== null &&
      resetVoucherErrorCount !== 0 &&
      resetVoucherErrorCount !== lastProcessedResetVoucherErrorCount.current
    ) {
      openNotification('error', 'Failed to Reset Voucher', resetVoucherError);
      setIsResetConfirmOpen(false);
      setSelectedVoucherId(null);
      lastProcessedResetVoucherErrorCount.current = resetVoucherErrorCount;
    }
  }, [resetVoucherError, resetVoucherErrorCount, openNotification]);

  // Handle toggle voucher success
  useEffect(() => {
    if (
      toggleVoucherResponse !== null &&
      toggleVoucherResponse !== lastProcessedToggleVoucherResponse.current
    ) {
      const responseData: any = toggleVoucherResponse.responseData;
      const voucherId = responseData?.intPkVoucherID;
      
      // Only process if we have a valid response with voucher ID
      if (voucherId) {
        hasProcessedToggleSuccess.current = true;
        const isEnabled = responseData?.enable === 1;
        
        // Optimistically update the voucher in the local state
        if (selectedVoucherToggle) {
          setVouchers((prevVouchers) =>
            prevVouchers.map((voucher) =>
              voucher.intPkVoucherID === selectedVoucherToggle.id
                ? { ...voucher, enable: selectedVoucherToggle.enable }
                : voucher
            )
          );
        }
        
        openNotification(
          'success',
          'Voucher Status Updated',
          `Voucher has been ${isEnabled ? 'enabled' : 'disabled'} successfully`
        );
        
        lastProcessedToggleVoucherResponse.current = toggleVoucherResponse;
        setIsVoucherToggleConfirmOpen(false);
        setSelectedVoucherToggle(null);
        
        // Refresh vouchers list to get latest data from server
        if (intPkBatchID) {
          fetchVouchersData({
            id: Number(intPkBatchID),
            page: pagination.current,
            pageSize: pagination.pageSize,
            searchMac: searchMac || undefined,
            searchVoucher: searchVoucher || undefined,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
          });
        }
      }
    }
  }, [toggleVoucherResponse]);

  // Reset the flag when toggleVoucherResponse becomes null
  useEffect(() => {
    if (toggleVoucherResponse === null) {
      hasProcessedToggleSuccess.current = false;
    }
  }, [toggleVoucherResponse]);

  // Handle toggle voucher error
  useEffect(() => {
    if (
      toggleVoucherError !== null &&
      toggleVoucherErrorCount !== 0 &&
      toggleVoucherErrorCount !== lastProcessedToggleVoucherErrorCount.current
    ) {
      openNotification('error', 'Failed to Toggle Voucher Status', toggleVoucherError);
      lastProcessedToggleVoucherErrorCount.current = toggleVoucherErrorCount;
      setIsVoucherToggleConfirmOpen(false);
      setSelectedVoucherToggle(null);
      // Refresh vouchers list to revert the UI state
      if (intPkBatchID) {
        setTimeout(() => {
          fetchVouchersData({
            id: Number(intPkBatchID),
            page: pagination.current,
            pageSize: pagination.pageSize,
            searchMac: searchMac || undefined,
            searchVoucher: searchVoucher || undefined,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
          });
        }, 500);
      }
    }
  }, [toggleVoucherError, toggleVoucherErrorCount]);

  const getSpeedDisplay = (speedDown: number, speedUp: number, unit: string) => {
    return `${speedDown} / ${speedUp} ${unit}bps`;
  };

  const getDataVolumeDisplay = () => {
    if (!batchDetails) return 'Unlimited';
    if (batchDetails.maxDataVolumeLimit && batchDetails.maxDataVolumeUnit) {
      return `${batchDetails.maxDataVolumeLimit} ${batchDetails.maxDataVolumeUnit}B`;
    }
    return 'Unlimited';
  };

  const getDurationDisplay = () => {
    if (!batchDetails) return '-';
    if (batchDetails.packageDurationEnum === 'DAYS') {
      return `${batchDetails.packageDurationDays} days`;
    } else if (batchDetails.packageDurationEnum === 'HOURS') {
      return `${batchDetails.packageDurationHours} hours`;
    }
    return '-';
  };

  const getVoucherDisplay = () => {
    if (!batchDetails) return '-';
    return `${batchDetails.voucherCharactersLimit} chars / ${batchDetails.voucherNumberType}`;
  };

  const handleToggleChange = (checked: boolean) => {
    const newValue = checked ? 1 : 0;
    setSwitchChecked(checked); // Update switch immediately for visual feedback
    setPendingToggleValue(newValue);
    setIsToggleConfirmOpen(true);
  };

  const handleToggleConfirm = () => {
    if (pendingToggleValue !== null && intPkBatchID) {
      toggleStatusData({
        id: Number(intPkBatchID),
        enable: pendingToggleValue,
      } as any);
    }
  };

  const handleToggleCancel = () => {
    setIsToggleConfirmOpen(false);
    setPendingToggleValue(null);
    // Revert switch to original state
    if (batchDetails) {
      setSwitchChecked(Boolean(batchDetails.enable));
    }
  };

  const handleResetVoucher = (voucherId: number | string) => {
    setSelectedVoucherId(voucherId);
    setIsResetConfirmOpen(true);
  };

  const handleResetConfirm = () => {
    if (selectedVoucherId !== null) {
      resetVoucherData({ id: Number(selectedVoucherId) });
    }
  };

  const handleToggleVoucher = (voucherId: number | string, enable: number) => {
    setSelectedVoucherToggle({ id: voucherId, enable });
    setIsVoucherToggleConfirmOpen(true);
  };

  const handleVoucherToggleConfirm = () => {
    if (selectedVoucherToggle) {
      toggleVoucherData({ id: Number(selectedVoucherToggle.id), enable: selectedVoucherToggle.enable });
    }
  };

  const handleVoucherToggleCancel = () => {
    setIsVoucherToggleConfirmOpen(false);
    setSelectedVoucherToggle(null);
  };

  const handleResetCancel = () => {
    setIsResetConfirmOpen(false);
    setSelectedVoucherId(null);
  };

  const handleDownloadCSV = () => {
    if (vouchers.length === 0) {
      openNotification('warning', 'No Data', 'No vouchers to download');
      return;
    }

    // Create CSV headers
    const headers = [
      'Voucher Number',
      'Status',
      'Created Date',
      'Used Date',
      'MAC Address',
      'Expiry Date',
      'Total Upload',
      'Total Download',
      'Total Uptime',
    ];

    // Create CSV rows
    const rows = vouchers.map((voucher) => [
      voucher.voucherNumber,
      voucher.voucherStatus,
      voucher.dateCreated ? new Date(voucher.dateCreated).toLocaleString() : '-',
      voucher.voucherUsedDate ? new Date(voucher.voucherUsedDate).toLocaleString() : '-',
      voucher.userMacAddress || '-',
      voucher.expiryDate ? new Date(voucher.expiryDate).toLocaleString() : '-',
      voucher.formattedTotalUploadedBytes || '0 B',
      voucher.formattedTotalDownloadedBytes || '0 B',
      voucher.formattedTotalUptime || '0 seconds',
    ]);

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `batch-${batchDetails?.batchName || intPkBatchID}-vouchers-${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    openNotification('success', 'Download Started', 'CSV file download started');
  };

  // Helper functions for PDF formatting (similar to VoucherCard)
  const getSpeedDisplayForPDF = (speedDown?: number, speedUp?: number, speedUnit?: 'K' | 'M' | 'G') => {
    if (!speedDown || !speedUp || !speedUnit) return 'N/A';
    const unitLabel = speedUnit === 'K' ? 'Kbps' : speedUnit === 'M' ? 'Mbps' : 'Gbps';
    return `${speedDown} / ${speedUp} ${unitLabel}`;
  };

  const getDataVolumeDisplayForPDF = (voucher: BatchVouchersTableDataType) => {
    if (!voucher.batchMaxDataVolumeLimit || !voucher.batchMaxDataVolumeUnit) {
      return 'Unlimited';
    }
    const unitLabel =
      voucher.batchMaxDataVolumeUnit === 'K' ? 'KB' : voucher.batchMaxDataVolumeUnit === 'M' ? 'MB' : 'GB';
    return `${voucher.batchMaxDataVolumeLimit} ${unitLabel}`;
  };

  const getDurationDisplayForPDF = (voucher: BatchVouchersTableDataType) => {
    const enumValue = voucher.batchPackageDurationEnum;
    const days = voucher.batchPackageDurationDays;
    const hours = voucher.batchPackageDurationHours;

    const normalizedEnum = enumValue ? String(enumValue).toUpperCase() : null;

    if (normalizedEnum === 'DAYS' && days !== null && days !== undefined) {
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }

    if (normalizedEnum === 'HOURS' && hours !== null && hours !== undefined) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    if (days !== null && days !== undefined && days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }

    if (hours !== null && hours !== undefined && hours > 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    return 'N/A';
  };

  const handleDownloadPDF = () => {
    if (vouchers.length === 0) {
      openNotification('warning', 'No Data', 'No vouchers to download');
      return;
    }

    // Create a card-based PDF layout
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      openNotification('error', 'Download Failed', 'Please allow popups to download PDF');
      return;
    }

    const batchName = batchDetails?.batchName || `Batch ${intPkBatchID}`;

    // Generate card HTML for each voucher
    const cardsHtml = vouchers
      .map((voucher) => {
        const isUsed = voucher.voucherStatus === 'USED';
        const statusColor = isUsed ? '#ff4d4f' : '#52c41a';
        const statusText = isUsed ? 'Used' : 'Unused';

        return `
          <div class="voucher-card-pdf">
            <div class="card-header-pdf">
              <span class="status-badge-pdf" style="background-color: ${
                statusColor === '#ff4d4f' ? '#fff1f0' : '#f6ffed'
              }; color: ${statusColor}; border: 1px solid ${statusColor};">
                ${statusText}
              </span>
            </div>
            <div class="card-divider-pdf"></div>
            <div class="voucher-code-section-pdf">
              <div class="voucher-code-label-pdf">Voucher Code</div>
              <div class="voucher-code-box-pdf">${voucher.voucherNumber}</div>
            </div>
            <div class="card-divider-pdf"></div>
            <div class="card-details-pdf">
              ${
                voucher.batchSpeedDown || voucher.batchSpeedUp
                  ? `
                <div class="detail-row-pdf">
                  <span class="detail-label-pdf">Speed:</span>
                  <span class="detail-value-pdf highlight">${getSpeedDisplayForPDF(
                    voucher.batchSpeedDown,
                    voucher.batchSpeedUp,
                    voucher.batchSpeedUnit,
                  )}</span>
                </div>
              `
                  : ''
              }
              <div class="detail-row-pdf">
                <span class="detail-label-pdf">Data Limit:</span>
                <span class="detail-value-pdf highlight">${getDataVolumeDisplayForPDF(voucher)}</span>
              </div>
              <div class="detail-row-pdf">
                <span class="detail-label-pdf">Duration:</span>
                <span class="detail-value-pdf highlight">${getDurationDisplayForPDF(voucher)}</span>
              </div>
              <div class="card-divider-pdf"></div>
              ${
                voucher.voucherUsedDate
                  ? `
                <div class="detail-row-pdf">
                  <span class="detail-label-pdf">Used Date:</span>
                  <span class="detail-value-pdf">${
                    voucher.voucherUsedDate ? formatDate(voucher.voucherUsedDate) : '-'
                  }</span>
                </div>
              `
                  : ''
              }
              ${
                voucher.expiryDate
                  ? `
                <div class="detail-row-pdf">
                  <span class="detail-label-pdf">Expiry Date:</span>
                  <span class="detail-value-pdf">${voucher.expiryDate ? formatDate(voucher.expiryDate) : '-'}</span>
                </div>
              `
                  : ''
              }
              ${
                voucher.userMacAddress
                  ? `
                <div class="detail-row-pdf">
                  <span class="detail-label-pdf">MAC Address:</span>
                  <span class="detail-value-pdf mac-address-pdf">${voucher.userMacAddress}</span>
                </div>
              `
                  : ''
              }
              <div class="card-divider-pdf"></div>
              <div class="usage-section-pdf">
                <div class="usage-item-pdf">
                  <div class="usage-label-pdf">Downloaded</div>
                  <div class="usage-value-pdf">${voucher.formattedTotalDownloadedBytes || '0 B'}</div>
                </div>
                <div class="usage-item-pdf">
                  <div class="usage-label-pdf">Uploaded</div>
                  <div class="usage-value-pdf">${voucher.formattedTotalUploadedBytes || '0 B'}</div>
                </div>
              </div>
              ${
                voucher.formattedTotalUptime
                  ? `
                <div class="detail-row-pdf" style="margin-top: 4px;">
                  <span class="detail-label-pdf">Uptime:</span>
                  <span class="detail-value-pdf">${voucher.formattedTotalUptime}</span>
                </div>
              `
                  : ''
              }
            </div>
          </div>
        `;
      })
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Vouchers - ${batchName}</title>
          <style>
            * {
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 10px;
              background: #f5f5f5;
            }
            .header-pdf {
              background: white;
              padding: 10px;
              margin-bottom: 10px;
              border-radius: 4px;
              box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            }
            h1 {
              color: #333;
              margin: 0;
              font-size: 16px;
              font-weight: 600;
            }
            .cards-container-pdf {
              display: grid;
              grid-template-columns: repeat(6, 1fr);
              gap: 4px;
              margin-top: 8px;
            }
            .voucher-card-pdf {
              background: white;
              border: 1px solid #e8e8e8;
              border-radius: 3px;
              padding: 5px;
              page-break-inside: avoid;
              break-inside: avoid;
              min-height: 0;
            }
            .card-header-pdf {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 2px;
            }
            .status-badge-pdf {
              padding: 2px 6px;
              border-radius: 2px;
              font-weight: 600;
              font-size: 10px;
            }
            .card-divider-pdf {
              height: 1px;
              background: #e8e8e8;
              margin: 3px 0;
            }
            .voucher-code-section-pdf {
              text-align: center;
              padding: 4px 0;
            }
            .voucher-code-label-pdf {
              font-size: 9px;
              color: #8c8c8c;
              margin-bottom: 3px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            .voucher-code-box-pdf {
              border: 1.5px solid #d9d9d9;
              border-radius: 3px;
              padding: 5px;
              background: #fafafa;
              font-size: 13px;
              font-weight: 700;
              font-family: 'Courier New', monospace;
              letter-spacing: 0.5px;
              color: #262626;
            }
            .card-details-pdf {
              flex: 1;
            }
            .detail-row-pdf {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 3px;
              gap: 4px;
            }
            .detail-label-pdf {
              font-size: 9px;
              color: #8c8c8c;
              font-weight: 500;
              min-width: 55px;
            }
            .detail-value-pdf {
              font-size: 9px;
              color: #262626;
              text-align: right;
              flex: 1;
              word-break: break-word;
            }
            .detail-value-pdf.highlight {
              font-weight: 600;
              color: #1890ff;
            }
            .mac-address-pdf {
              font-family: 'Courier New', monospace;
              font-size: 8px;
            }
            .usage-section-pdf {
              display: flex;
              gap: 5px;
              justify-content: space-between;
              margin-top: 3px;
            }
            .usage-item-pdf {
              flex: 1;
              text-align: center;
              padding: 3px;
              background: #f5f5f5;
              border-radius: 2px;
            }
            .usage-label-pdf {
              font-size: 8px;
              color: #8c8c8c;
              margin-bottom: 2px;
              text-transform: uppercase;
              letter-spacing: 0.2px;
            }
            .usage-value-pdf {
              font-size: 10px;
              font-weight: 600;
              color: #1890ff;
            }
            @media print {
              body {
                background: white;
                margin: 0;
                padding: 0.3cm;
              }
              .header-pdf {
                box-shadow: none;
                padding: 2px 3px;
                margin-bottom: 2px;
                page-break-after: avoid;
              }
              h1 {
                font-size: 11px;
                margin: 0;
              }
              .cards-container-pdf {
                grid-template-columns: repeat(6, 1fr);
                gap: 3px;
                margin-top: 2px;
                margin-bottom: 0;
              }
              .voucher-card-pdf {
                box-shadow: none;
                border: 1px solid #ddd;
                padding: 5px;
                height: auto;
                max-height: none;
                display: flex;
                flex-direction: column;
                overflow: visible;
              }
              .card-header-pdf {
                margin-bottom: 2px;
              }
              .status-badge-pdf {
                padding: 1px 5px;
                font-size: 9px;
              }
              .card-divider-pdf {
                margin: 2px 0;
                height: 1px;
              }
              .voucher-code-section-pdf {
                padding: 3px 0;
              }
              .voucher-code-label-pdf {
                font-size: 8px;
                margin-bottom: 2px;
              }
              .voucher-code-box-pdf {
                padding: 4px;
                font-size: 12px;
              }
              .detail-row-pdf {
                margin-bottom: 2px;
              }
              .detail-label-pdf {
                font-size: 8px;
                min-width: 50px;
              }
              .detail-value-pdf {
                font-size: 8px;
              }
              .mac-address-pdf {
                font-size: 7px;
              }
              .usage-section-pdf {
                margin-top: 2px;
                gap: 4px;
              }
              .usage-item-pdf {
                padding: 2px;
              }
              .usage-label-pdf {
                font-size: 7px;
              }
              .usage-value-pdf {
                font-size: 9px;
              }
              @page {
                margin: 0.3cm;
                size: A4 portrait;
              }
            }
          </style>
        </head>
        <body>
          <div class="header-pdf">
            <h1>Vouchers Report - ${batchName}</h1>
          </div>
          <div class="cards-container-pdf">
            ${cardsHtml}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();

    openNotification('success', 'PDF Generated', 'PDF is being generated. Please use the print dialog to save as PDF.');
  };

  if (apiLoading) {
    return (
      <>
        {contextHolder}
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>Loading batch details...</Typography>
        </div>
      </>
    );
  }

  if (apiError && !apiLoading) {
    return (
      <>
        {contextHolder}
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty
            description={<Typography.Text type="danger">{apiError || 'Failed to load batch details'}</Typography.Text>}
          />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/batches')}
            style={{ marginTop: '16px' }}
          >
            Back to Batches
          </Button>
        </div>
      </>
    );
  }

  if (!batchDetails) {
    return (
      <>
        {contextHolder}
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="Batch not found" />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/batches')}
            style={{ marginTop: '16px' }}
          >
            Back to Batches
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: '16px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/batches')}>
          Back
        </Button>
      </div>

      <Card
        title="Batch Details"
        style={{ marginBottom: '16px' }}
        extra={
          <Tag color={batchDetails.enable === 0 ? 'red' : batchDetails.batchProcessStatus === 'PROCESSED' ? 'success' : 'warning'}>
            {batchDetails.enable === 0 ? 'Disabled' : batchDetails.batchProcessStatus === 'PROCESSED' ? 'Active' : 'PENDING'}
          </Tag>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Table
              dataSource={[
                {
                  key: '1',
                  label: 'Batch Name',
                  value: batchDetails.batchName,
                },
                {
                  key: '2',
                  label: 'Package Name',
                  value: batchDetails.packageName,
                },
                {
                  key: '3',
                  label: 'Vouchers Quantity',
                  value: batchDetails.batchLength,
                },
                {
                  key: '4',
                  label: 'Download / Upload Speed',
                  value: getSpeedDisplay(batchDetails.speedDown, batchDetails.speedUp, batchDetails.speedUnit),
                },
                {
                  key: '5',
                  label: 'Max Data Volume',
                  value: getDataVolumeDisplay(),
                },
                {
                  key: '6',
                  label: 'Date Created',
                  value: batchDetails.dateCreated ? formatDate(batchDetails.dateCreated) : '-',
                },
              ]}
              columns={[
                {
                  title: 'Field',
                  dataIndex: 'label',
                  key: 'label',
                  width: '50%',
                  render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  key: 'value',
                  width: '50%',
                },
              ]}
              pagination={false}
              showHeader={false}
              size="small"
            />
          </Col>
          <Col xs={24} lg={12}>
            <Table
              dataSource={[
                {
                  key: '1',
                  label: 'Used Vouchers',
                  value: batchDetails.usedVouchers ?? 0,
                },
                {
                  key: '2',
                  label: 'Remaining Vouchers',
                  value: batchDetails.remainingVouchers ?? batchDetails.batchLength,
                },
                {
                  key: '3',
                  label: 'Voucher',
                  value: getVoucherDisplay(),
                },
                {
                  key: '4',
                  label: 'Duration',
                  value: getDurationDisplay(),
                },
                {
                  key: '5',
                  label: 'Status',
                  value: (
                    <Space>
                      <Typography.Text type={batchDetails.enable === 1 ? 'success' : 'secondary'}>
                        {batchDetails.enable === 1 ? 'Enabled' : 'Disabled'}
                      </Typography.Text>
                      {getUserType() === 'ADMIN' && (
                        <Switch checked={switchChecked} onChange={handleToggleChange} disabled={toggleStatusLoading} />
                      )}
                    </Space>
                  ),
                },
              ]}
              columns={[
                {
                  title: 'Field',
                  dataIndex: 'label',
                  key: 'label',
                  width: '50%',
                  render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  key: 'value',
                  width: '50%',
                },
              ]}
              pagination={false}
              showHeader={false}
              size="small"
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title="Confirm Status Change"
        open={isToggleConfirmOpen}
        onOk={handleToggleConfirm}
        onCancel={handleToggleCancel}
        confirmLoading={toggleStatusLoading}
        okText="Confirm"
        cancelText="Cancel"
        width={400}
      >
        <Typography>Are you sure you want to {pendingToggleValue === 1 ? 'enable' : 'disable'} this batch?</Typography>
      </Modal>

      <Modal
        title="Confirm Reset Voucher"
        open={isResetConfirmOpen}
        onOk={handleResetConfirm}
        onCancel={handleResetCancel}
        confirmLoading={resetVoucherLoading}
        okText="Confirm"
        cancelText="Cancel"
        width={400}
      >
        <Typography>Are you sure you want to reset this voucher status to reuse?</Typography>
      </Modal>

      <Modal
        title="Confirm Toggle Voucher Status"
        open={isVoucherToggleConfirmOpen}
        onOk={handleVoucherToggleConfirm}
        onCancel={handleVoucherToggleCancel}
        confirmLoading={toggleVoucherLoading}
        okText="Confirm"
        cancelText="Cancel"
        width={400}
      >
        <Typography>
          Are you sure you want to {selectedVoucherToggle?.enable === 1 ? 'enable' : 'disable'} this voucher?
        </Typography>
      </Modal>

      {batchDetails && (
        <Card
          title="Vouchers"
          style={{ marginTop: '16px' }}
          extra={
            vouchers.length > 0 ? (
              <Space>
                <Button icon={<FileExcelOutlined />} onClick={handleDownloadCSV} disabled={vouchersLoading}>
                  Download CSV
                </Button>
                <Button icon={<FilePdfOutlined />} onClick={handleDownloadPDF} disabled={vouchersLoading}>
                  Download PDF
                </Button>
              </Space>
            ) : null
          }
        >
          <BatchVouchersTable
            vouchers={vouchers}
            onResetVoucher={handleResetVoucher}
            onToggleVoucher={handleToggleVoucher}
            onSearch={(searchBy, value) => {
              if (searchBy === 'mac') {
                setSearchMac(value);
                setPagination((prev) => ({ ...prev, current: 1 }));
                if (intPkBatchID) {
                  fetchVouchersData({
                    id: Number(intPkBatchID),
                    page: 1,
                    pageSize: pagination.pageSize,
                    searchMac: value || undefined,
                    searchVoucher: searchVoucher || undefined,
                    sortBy: sortBy || undefined,
                    sortOrder: sortOrder || undefined,
                  } as any);
                }
              } else if (searchBy === 'voucher') {
                setSearchVoucher(value);
                setPagination((prev) => ({ ...prev, current: 1 }));
                if (intPkBatchID) {
                  fetchVouchersData({
                    id: Number(intPkBatchID),
                    page: 1,
                    pageSize: pagination.pageSize,
                    searchMac: searchMac || undefined,
                    searchVoucher: value || undefined,
                    sortBy: sortBy || undefined,
                    sortOrder: sortOrder || undefined,
                  } as any);
                }
              }
            }}
            onPaginationChange={(page, pageSize) => {
              setPagination((prev) => ({ ...prev, current: page, pageSize }));
              // Fetch with the clicked page so the request always sends the correct page (avoids race with effect/state)
              if (intPkBatchID) {
                fetchVouchersData({
                  id: Number(intPkBatchID),
                  page,
                  pageSize,
                  searchMac: searchMac || undefined,
                  searchVoucher: searchVoucher || undefined,
                  sortBy: sortBy || undefined,
                  sortOrder: sortOrder || undefined,
                } as any);
              }
            }}
            onSortChange={(field, order) => {
              const fieldMap: Record<string, string> = {
                voucherUsedDate: 'voucherUsedDate',
                expiryDate: 'expiryDate',
                formattedTotalDownloadedBytes: 'totalDownloadedBytes',
                formattedTotalUploadedBytes: 'totalUploadedBytes',
              };
              const backendField = fieldMap[field] || undefined;
              setSortBy(backendField);
              setSortOrder(order);
              setPagination((prev) => ({ ...prev, current: 1 }));
              if (intPkBatchID) {
                fetchVouchersData({
                  id: Number(intPkBatchID),
                  page: 1,
                  pageSize: pagination.pageSize,
                  searchMac: searchMac || undefined,
                  searchVoucher: searchVoucher || undefined,
                  sortBy: backendField,
                  sortOrder: order ?? undefined,
                } as any);
              }
            }}
            pagination={pagination}
            loading={vouchersLoading}
            sortBy={(() => {
              // Map backend field names back to frontend field names for display
              const reverseFieldMap: Record<string, string> = {
                voucherUsedDate: 'voucherUsedDate',
                expiryDate: 'expiryDate',
                totalDownloadedBytes: 'formattedTotalDownloadedBytes',
                totalUploadedBytes: 'formattedTotalUploadedBytes',
              };
              return sortBy ? reverseFieldMap[sortBy] : undefined;
            })()}
            sortOrder={sortOrder}
            searchMac={searchMac}
            searchVoucher={searchVoucher}
          />
        </Card>
      )}
    </>
  );
};
export default SingleBatchComponent;
